import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import CallOverview from "../audioIntelligence/CallOverview";
import axiosInstance from "../../../services/axiosInterceptors";
import nipponBotApiEndpoints from "../../../services/nipponBotApiEndpoints";
import { useParams } from "react-router-dom";
import Loader from "../../../components/AnimatedLoader";
import { theme as colorTheme } from "../../../Convoze.theme";
import ParentWrapper from "../../../components/ParentWrapper";
import AudioAnalyticsMui from "../audioIntelligence/Entities";
import NipponInterviewSummary from "./NipponInterviewSummary";
import NipponInterviewPhases from "./NipponInterviewPhases";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`button-tabpanel-${index}`}
      aria-labelledby={`button-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const getCandidateDisplay = (candidate) => {
  if (!candidate) return "";
  return candidate.fullName || "Unnamed Candidate";
};

const getAssignedCandidateSummary = (payload) => {
  const sources = [
    payload?.assignedCandidate,
    payload?.candidate,
    payload?.linkedCandidate,
    payload?.data?.assignedCandidate,
    payload?.data?.candidate,
    payload?.data?.linkedCandidate
  ].filter(Boolean);
  return sources[0] || null;
};

const getAssignedCandidateId = (payload) => {
  const candidate = getAssignedCandidateSummary(payload);
  return candidate?.id || candidate?._id || payload?.candidateId || payload?.data?.candidateId || null;
};

const getCurrentAssignedCandidate = (detail, candidates) => {
  const identifiers = [
    detail?.candidateId,
    detail?.candidate?._id,
    detail?.candidate?.id,
    detail?.candidateDetails?._id,
    detail?.candidateDetails?.id,
    detail?.interviewData?.candidateId,
    detail?.interviewData?.candidate?._id,
    detail?.interviewData?.candidate?.id
  ].filter(Boolean);

  const matchedById = candidates.find((candidate) => identifiers.includes(candidate.id));
  if (matchedById) return matchedById;

  return detail?.candidate || detail?.candidateDetails || detail?.interviewData?.candidate || null;
};

const getCandidateRowsFromListResponse = (response) => {
  const payload = response?.data?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

// Nippon-Bot-only detail page shown in place of the shared AudioIntelligence
// tab set - reuses the generic CallOverview/Entities sub-components but swaps
// the QA tab for the interview phases/summary this client's pipeline produces.
const NipponAudioIntelligence = () => {
  const { id } = useParams();
  const token = localStorage.getItem("token");
  const [value, setValue] = useState(0);
  const [data, setData] = useState({});
  const [overview, setOverview] = useState({});
  const [customerAnalytics, setCustomerAnalytics] = useState({});
  const [agentAnalytics, setAgentAnalytics] = useState({});
  const [qaParameters, setQaParameters] = useState([]);
  const [transcript, setTranscript] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entities, setEntities] = useState([]);
  const [createdAt, setCreatedAt] = useState("");
  const [interviewData, setInterviewData] = useState({});
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState("");
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [assignNotice, setAssignNotice] = useState({ type: "info", message: "" });

  useEffect(() => {
    getData();
    loadCandidates();
  }, []);

  const loadCandidates = async () => {
    setCandidatesLoading(true);
    try {
      const res = await axiosInstance.get(nipponBotApiEndpoints.candidateList, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCandidates(getCandidateRowsFromListResponse(res));
    } catch (error) {
      setAssignNotice({
        type: "error",
        message: error?.response?.data?.message || "Failed to load candidate list for assignment."
      });
    } finally {
      setCandidatesLoading(false);
    }
  };

  const getData = async () => {
    try {
      const res = await axiosInstance.get(
        `${nipponBotApiEndpoints?.interactionDetail}/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const detail = res?.data?.data[0] || {};
      setData(detail);
      setQaParameters(detail?.callAdherence || []);
      setOverview(detail?.overallSummary);
      setCustomerAnalytics(detail?.customerSummary);
      setAgentAnalytics(detail?.agentSummary);
      setTranscript(detail?.transcript);
      setEntities(detail?.entities);
      setCreatedAt(detail?.createdAt);
      setInterviewData(detail?.interviewData || {});
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setAssignNotice({
        type: "error",
        message: error?.response?.data?.message || "Failed to load report details."
      });
      return error;
    }
  };

  const currentAssignedCandidate = useMemo(
    () => getCurrentAssignedCandidate(data, candidates),
    [data, candidates]
  );

  const filteredCandidates = useMemo(() => {
    const query = candidateSearch.trim().toLowerCase();
    if (!query) return candidates;
    return candidates.filter((candidate) => (candidate.fullName || "").toLowerCase().includes(query));
  }, [candidateSearch, candidates]);

  const handleAssignCandidate = async () => {
    if (!selectedCandidate?.id) {
      setAssignNotice({ type: "error", message: "Select a candidate before assigning this recording." });
      return;
    }

    setAssigning(true);
    try {
      const response = await axiosInstance.post(
        nipponBotApiEndpoints.assignInterviewCandidate,
        {
          transcriptionId: id,
          recordingId: data?.recordingId || data?._id || null,
          candidateId: selectedCandidate.id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const assignedCandidate = getAssignedCandidateSummary(response?.data?.data || response?.data) || selectedCandidate;
      setAssignNotice({
        type: "success",
        message: `Recording assigned to ${assignedCandidate.fullName || selectedCandidate.fullName}.`
      });
      setSelectedCandidate(null);
      setCandidateSearch("");
      await getData();
      await loadCandidates();
    } catch (error) {
      setAssignNotice({
        type: "error",
        message: error?.response?.data?.status?.value || error?.response?.data?.message || "Failed to assign candidate."
      });
    } finally {
      setAssigning(false);
    }
  };

  const tabLabels = [
    "Overview",
    "QA Parameters",
  ];

  return (
    <>
      {loading ? (
        <ParentWrapper sx={{ minHeight: "calc(100vh - 45px)" }}>
          <Loader />
        </ParentWrapper>
      ) : (
        <ParentWrapper sx={{ minHeight: "calc(100vh - 45px)" }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #dbe4f0", mb: 3, backgroundColor: "#f8fbff" }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>Assign Candidate</Typography>
                <Typography variant="body2" sx={{ color: "#475569" }}>
                  Manually assign this interview report to a candidate by name. Selection always requires an explicit candidate pick.
                </Typography>
              </Box>

              {currentAssignedCandidate ? (
                <Alert severity="info">
                  Currently assigned to {getCandidateDisplay(currentAssignedCandidate)}.
                </Alert>
              ) : null}

              {assignNotice.message ? <Alert severity={assignNotice.type}>{assignNotice.message}</Alert> : null}

              <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1.7fr auto" }, alignItems: "start" }}>
                <Autocomplete
                  options={filteredCandidates}
                  loading={candidatesLoading}
                  value={selectedCandidate}
                  onChange={(event, newValue) => setSelectedCandidate(newValue)}
                  inputValue={candidateSearch}
                  onInputChange={(event, newInputValue) => setCandidateSearch(newInputValue)}
                  getOptionLabel={getCandidateDisplay}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText={candidateSearch.trim() ? "No candidates match this name." : "Type a candidate name to search."}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Candidate By Name"
                      placeholder="Start typing the candidate full name"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {candidatesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Button
                  variant="contained"
                  onClick={handleAssignCandidate}
                  disabled={assigning || !selectedCandidate?.id}
                  sx={{ minHeight: 56, textTransform: "none" }}
                >
                  {assigning ? <><CircularProgress size={18} sx={{ color: "white", mr: 1 }} />Assigning...</> : currentAssignedCandidate ? "Assign / Reassign Candidate" : "Assign Candidate"}
                </Button>
              </Box>
            </Stack>
          </Paper>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {tabLabels?.map((label, index) => (
              <Button
                key={index}
                variant={value === index ? "contained" : "outlined"}
                onClick={() => setValue(index)}
                sx={{
                  textTransform: "capitalize",
                  fontWeight: 550,
                  fontSize: "small",
                  fontFamily: "sans-serif",
                  borderRadius: "10px",
                  bgcolor:
                    value == index
                      ? colorTheme("palette", "primary").value
                      : "",
                }}
              >
                {label}
              </Button>
            ))}
          </Box>

          <CustomTabPanel value={value} index={0}>
            <CallOverview
              overview={overview}
              data={data}
              agentAnalytics={agentAnalytics}
              customerAnalytics={customerAnalytics}
              transcript={transcript}
              setValue={setValue}
            />
          </CustomTabPanel>

          <CustomTabPanel value={value} index={1}>
            <NipponInterviewSummary interviewData={interviewData} />
            <NipponInterviewPhases
              phases={qaParameters}
              reportId={id}
              recordingDateTime={data?.callDate}
              uploadedDateTime={createdAt}
              fileName={data?.fileName || data?.originalFileName}
              resumeInterviewComparison={data?.resumeInterviewComparison}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={4}>
            <AudioAnalyticsMui entitiesData={entities} />
          </CustomTabPanel>
        </ParentWrapper>
      )}
    </>
  );
};

export default NipponAudioIntelligence;
